import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, Copy, ShieldAlert, Webhook, Send, TestTube, Play, Terminal } from "lucide-react";

export default function Developers() {
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [openKey, setOpenKey] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: "" });
  const [newlyIssued, setNewlyIssued] = useState(null);

  const [openWh, setOpenWh] = useState(false);
  const [whForm, setWhForm] = useState({ url: "", description: "", events: [], is_active: true });
  const [activeWh, setActiveWh] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  // API Playground state
  const PLAYGROUND_ENDPOINTS = [
    { method: "POST", path: "/api/verify/send", body: '{ "channel": "sms", "to": "+919999999999", "length": 6 }' },
    { method: "POST", path: "/api/verify/check", body: '{ "verify_id": "REPLACE_ME", "code": "123456" }' },
    { method: "GET",  path: "/api/lookup/+919876543210", body: "" },
    { method: "GET",  path: "/api/segments", body: "" },
    { method: "POST", path: "/api/segments/preview", body: '{ "name": "VIPs", "rules": { "op": "AND", "conditions": [{ "field": "tags", "operator": "contains", "value": "vip" }] } }' },
    { method: "POST", path: "/api/whatsapp/send-interactive", body: '{ "to": "+919876543210", "type": "button", "body_text": "Choose", "buttons": [{ "id": "yes", "title": "Yes" }, { "id": "no", "title": "No" }] }' },
    { method: "POST", path: "/api/ai/draft-reply", body: '{ "channel": "whatsapp", "tone": "friendly", "variants": 2, "thread_context": [{ "direction": "inbound", "body": "Hi, is my order ready?" }] }' },
    { method: "GET",  path: "/api/journeys", body: "" },
    { method: "POST", path: "/api/journeys/templates/welcome", body: "" },
    { method: "GET",  path: "/api/email-domains", body: "" },
    { method: "GET",  path: "/api/sender-ids", body: "" },
    { method: "GET",  path: "/api/dev/webhooks", body: "" },
    { method: "GET",  path: "/api/auth/me", body: "" },
  ];
  const [selectedEp, setSelectedEp] = useState(PLAYGROUND_ENDPOINTS[0]);
  const [playgroundBody, setPlaygroundBody] = useState(PLAYGROUND_ENDPOINTS[0].body);
  const [playgroundKey, setPlaygroundKey] = useState(() => localStorage.getItem("tez_last_api_key") || "");
  const [playgroundResp, setPlaygroundResp] = useState(null);
  const [playgroundLoading, setPlaygroundLoading] = useState(false);
  const [runHistory, setRunHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tez_pg_history") || "[]"); } catch { return []; }
  });

  const historyForKey = runHistory.filter(h => h.keyPrefix === (playgroundKey.split("_").slice(0, 2).join("_") || ""));

  const events = [
    "message.sent", "message.delivered", "message.failed", "message.received",
    "call.completed", "call.failed", "otp.sent", "otp.verified",
    "campaign.completed", "campaign.failed",
  ];

  const load = async () => {
    try {
      const [k, w] = await Promise.all([api.get("/dev/keys"), api.get("/dev/webhooks")]);
      setKeys(k.data); setWebhooks(w.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    if (!keyForm.name.trim()) return toast.error("Name required");
    try {
      const { data } = await api.post("/dev/keys", { name: keyForm.name });
      setNewlyIssued(data);
      // Auto-fill the playground with the new key so devs can test immediately
      setPlaygroundKey(data.full_key);
      try { localStorage.setItem("tez_last_api_key", data.full_key); } catch {}
      setOpenKey(false); setKeyForm({ name: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key? It can't be undone.")) return;
    await api.delete(`/dev/keys/${id}`); toast.success("Key revoked"); load();
  };

  const createWebhook = async () => {
    if (!whForm.url.trim() || whForm.events.length === 0) return toast.error("URL and at least one event required");
    try {
      await api.post("/dev/webhooks", whForm);
      toast.success("Webhook created"); setOpenWh(false);
      setWhForm({ url: "", description: "", events: [], is_active: true }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const testWh = async (id) => {
    try { await api.post(`/dev/webhooks/${id}/test`); toast.success("Test delivery fired — check deliveries"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openDeliveries = async (w) => {
    setActiveWh(w);
    const { data } = await api.get(`/dev/webhooks/${w.id}/deliveries`);
    setDeliveries(data);
  };
  const delWh = async (id) => {
    if (!window.confirm("Delete this webhook subscription?")) return;
    await api.delete(`/dev/webhooks/${id}`); toast.success("Deleted"); load();
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const runPlayground = async () => {
    if (!playgroundKey) return toast.error("Paste an API key or click a saved key first");
    setPlaygroundLoading(true); setPlaygroundResp(null);
    try {
      const url = process.env.REACT_APP_BACKEND_URL + selectedEp.path;
      const init = {
        method: selectedEp.method,
        headers: { "Authorization": "Bearer " + playgroundKey, "Content-Type": "application/json" },
      };
      if (selectedEp.method !== "GET" && playgroundBody.trim()) init.body = playgroundBody;
      const t0 = performance.now();
      const r = await fetch(url, init);
      const ms = Math.round(performance.now() - t0);
      const text = await r.text();
      let body;
      try { body = JSON.stringify(JSON.parse(text), null, 2); } catch { body = text; }
      const resp = { status: r.status, ms, body };
      setPlaygroundResp(resp);
      // Push to per-key history (max 20)
      const keyPrefix = playgroundKey.split("_").slice(0, 2).join("_");
      const entry = {
        id: Date.now(),
        keyPrefix,
        at: new Date().toISOString(),
        method: selectedEp.method,
        path: selectedEp.path,
        request_body: init.body || "",
        status: resp.status, ms: resp.ms,
        response_body: (resp.body || "").slice(0, 8000),
      };
      const next = [entry, ...runHistory.filter(h => h.keyPrefix === keyPrefix)].slice(0, 20)
        .concat(runHistory.filter(h => h.keyPrefix !== keyPrefix)); // keep other keys' history intact
      setRunHistory(next);
      try { localStorage.setItem("tez_pg_history", JSON.stringify(next)); } catch {}
    } catch (e) { setPlaygroundResp({ status: 0, ms: 0, body: String(e) }); }
    finally { setPlaygroundLoading(false); }
  };

  const loadHistoryEntry = (h) => {
    const ep = PLAYGROUND_ENDPOINTS.find(x => x.path === h.path && x.method === h.method) || { method: h.method, path: h.path, body: h.request_body };
    setSelectedEp(ep);
    setPlaygroundBody(h.request_body || "");
    setPlaygroundResp({ status: h.status, ms: h.ms, body: h.response_body });
    toast.success("Loaded from history");
  };
  const clearHistory = () => {
    if (!window.confirm("Clear saved Playground runs for this key?")) return;
    const keyPrefix = playgroundKey.split("_").slice(0, 2).join("_");
    const next = runHistory.filter(h => h.keyPrefix !== keyPrefix);
    setRunHistory(next);
    try { localStorage.setItem("tez_pg_history", JSON.stringify(next)); } catch {}
  };

  const curlSample = () => {
    const url = process.env.REACT_APP_BACKEND_URL + selectedEp.path;
    const auth = ` \\\n  -H "Authorization: Bearer ${playgroundKey || "<YOUR_API_KEY>"}"`;
    if (selectedEp.method === "GET") return `curl -sS "${url}"${auth}`;
    return `curl -sS -X ${selectedEp.method} "${url}"${auth} \\\n  -H "Content-Type: application/json" \\\n  -d '${playgroundBody.replace(/'/g, "'\\''")}'`;
  };

  return (
    <div className="space-y-6" data-testid="developers-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Developers</div>
        <h1 className="text-3xl font-black tracking-tighter">API Keys &amp; Webhooks</h1>
        <p className="text-xs text-muted-foreground mt-1">Issue programmatic keys and subscribe to real-time events with HMAC-signed webhooks.</p>
      </div>

      {/* API keys */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><KeyRound className="h-4 w-4" /><h2 className="text-lg font-semibold">API Keys</h2></div>
          <Dialog open={openKey} onOpenChange={setOpenKey}>
            <DialogTrigger asChild><Button className="rounded-sm gap-2" data-testid="new-api-key-button"><Plus className="h-4 w-4" /> New key</Button></DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Create API Key</DialogTitle><DialogDescription>You will see the full secret only once.</DialogDescription></DialogHeader>
              <div><Label>Name</Label><Input value={keyForm.name} onChange={(e) => setKeyForm({ name: e.target.value })} placeholder="e.g. Production Backend" className="rounded-sm" data-testid="api-key-name" /></div>
              <DialogFooter><Button onClick={createKey} className="rounded-sm" data-testid="api-key-create">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {newlyIssued && (
          <Card className="rounded-sm shadow-none border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-300 text-sm"><ShieldAlert className="h-4 w-4" /> Copy this now — you will not see it again</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-background border border-border rounded-sm p-2 break-all" data-testid="api-key-full">{newlyIssued.full_key}</code>
                <Button variant="outline" size="icon" className="rounded-sm h-8 w-8" onClick={() => copy(newlyIssued.full_key)}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="text-xs text-muted-foreground">Send it as <code>Authorization: Bearer &lt;key&gt;</code> on every API request. Store securely.</div>
              <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => setNewlyIssued(null)}>Dismiss</Button>
            </CardContent>
          </Card>
        )}

        <Card className="rounded-sm shadow-none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Name</th><th className="px-3 py-2">Prefix</th>
                  <th className="px-3 py-2">Scopes</th><th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} className="border-t border-border" data-testid={`api-key-row-${k.id}`}>
                    <td className="px-3 py-2 font-medium">{k.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}</td>
                    <td className="px-3 py-2 text-xs">{k.scopes.slice(0, 3).join(", ")}{k.scopes.length > 3 ? ` +${k.scopes.length - 3}` : ""}</td>
                    <td className="px-3 py-2 text-xs font-mono">{new Date(k.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{k.revoked_at ? <Badge variant="outline" className="rounded-sm text-[10px] border-red-300 text-red-700">REVOKED</Badge> : <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700">ACTIVE</Badge>}</td>
                    <td className="px-3 py-2 text-right">{!k.revoked_at && <Button variant="ghost" size="sm" className="rounded-sm text-red-600" onClick={() => revokeKey(k.id)} data-testid={`revoke-${k.id}`}><Trash2 className="h-3 w-3" /></Button>}</td>
                  </tr>
                ))}
                {keys.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-10">No API keys yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* Webhooks */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Webhook className="h-4 w-4" /><h2 className="text-lg font-semibold">Webhook Subscriptions</h2></div>
          <Dialog open={openWh} onOpenChange={setOpenWh}>
            <DialogTrigger asChild><Button className="rounded-sm gap-2" data-testid="new-webhook-button"><Plus className="h-4 w-4" /> New webhook</Button></DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader><DialogTitle>Add Webhook</DialogTitle><DialogDescription>We POST HMAC-signed JSON on every event you subscribe to.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label>Endpoint URL</Label><Input value={whForm.url} onChange={(e) => setWhForm({ ...whForm, url: e.target.value })} placeholder="https://your.app/webhooks/cpaas" className="rounded-sm font-mono text-xs" data-testid="webhook-url" /></div>
                <div><Label>Description</Label><Input value={whForm.description} onChange={(e) => setWhForm({ ...whForm, description: e.target.value })} className="rounded-sm" /></div>
                <div>
                  <Label>Events</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {events.map(ev => (
                      <label key={ev} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`event-cb-${ev}`}>
                        <input type="checkbox" checked={whForm.events.includes(ev)} onChange={(e) => {
                          const on = e.target.checked;
                          setWhForm(f => ({ ...f, events: on ? [...f.events, ev] : f.events.filter(x => x !== ev) }));
                        }} />
                        <span className="font-mono">{ev}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={createWebhook} className="rounded-sm" data-testid="webhook-create">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {webhooks.map(w => (
            <Card key={w.id} className="rounded-sm shadow-none" data-testid={`webhook-${w.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs break-all">{w.url}</div>
                  {w.is_active ? <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700">ACTIVE</Badge>
                    : <Badge variant="outline" className="rounded-sm text-[10px] border-zinc-300">DISABLED</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{w.description || "—"}</div>
                <div className="flex flex-wrap gap-1">{w.events.map(e => <Badge key={e} variant="outline" className="text-[10px] rounded-sm font-mono">{e}</Badge>)}</div>
                <div className="text-xs flex gap-4 text-muted-foreground">
                  <span className="text-emerald-700">✓ {w.success_count || 0}</span>
                  <span className="text-red-700">✗ {w.failure_count || 0}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">Signing secret: <code className="font-mono">{(w.signing_secret || "").slice(0, 14)}…</code>
                  <Button variant="ghost" size="sm" className="ml-2 h-6 rounded-sm" onClick={() => copy(w.signing_secret)}><Copy className="h-3 w-3" /></Button>
                </div>
                <div className="flex justify-end gap-1 pt-1">
                  <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => testWh(w.id)} data-testid={`test-webhook-${w.id}`}><TestTube className="h-3 w-3" /> Test</Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => openDeliveries(w)} data-testid={`deliveries-${w.id}`}><Send className="h-3 w-3" /> Log</Button>
                  <Button variant="ghost" size="sm" className="rounded-sm text-red-600" onClick={() => delWh(w.id)} data-testid={`delete-webhook-${w.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {webhooks.length === 0 && <Card className="rounded-sm shadow-none md:col-span-2"><CardContent className="text-center text-muted-foreground py-10">No webhook subscriptions yet.</CardContent></Card>}
        </div>

        <Dialog open={!!activeWh} onOpenChange={(v) => !v && setActiveWh(null)}>
          <DialogContent className="rounded-sm max-w-3xl">
            <DialogHeader><DialogTitle>Delivery log — {activeWh?.url}</DialogTitle></DialogHeader>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0"><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground"><th className="px-2 py-2">When</th><th className="px-2 py-2">Event</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Response</th></tr></thead>
              <tbody>
                {deliveries.map(d => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="px-2 py-1 font-mono">{new Date(d.created_at).toLocaleString()}</td>
                    <td className="px-2 py-1 font-mono">{d.event}</td>
                    <td className={`px-2 py-1 font-mono ${d.ok ? "text-emerald-700" : "text-red-700"}`}>{d.status_code || "err"}</td>
                    <td className="px-2 py-1 font-mono text-[10px] truncate max-w-[300px]">{d.response_body || "—"}</td>
                  </tr>
                ))}
                {deliveries.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-6">No deliveries yet.</td></tr>}
              </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      </section>

      {/* API Playground */}
      <section className="space-y-3" data-testid="api-playground">
        <div className="flex items-center gap-2"><Terminal className="h-4 w-4" /><h2 className="text-lg font-semibold">API Playground</h2></div>
        <Card className="rounded-sm shadow-none">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="flex items-center gap-2">API Key (paste the full key including secret)
                  {playgroundKey && <span className="text-[10px] text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 rounded-sm px-1.5 py-0.5">saved</span>}
                </Label>
                <div className="flex gap-2">
                  <Input value={playgroundKey} onChange={e => { setPlaygroundKey(e.target.value); try { localStorage.setItem("tez_last_api_key", e.target.value); } catch {} }} placeholder="tk_xxxx_yyyy…" className="rounded-sm font-mono text-xs" data-testid="playground-key" />
                  {playgroundKey && <Button variant="outline" size="sm" className="rounded-sm text-[10px]" onClick={() => { setPlaygroundKey(""); try { localStorage.removeItem("tez_last_api_key"); } catch {} }} data-testid="playground-clear-key">Clear</Button>}
                </div>
              </div>
              <div>
                <Label>Endpoint</Label>
                <select
                  value={selectedEp.path + selectedEp.method}
                  onChange={(e) => {
                    const ep = PLAYGROUND_ENDPOINTS.find(x => x.path + x.method === e.target.value);
                    setSelectedEp(ep); setPlaygroundBody(ep.body); setPlaygroundResp(null);
                  }}
                  className="h-9 w-full px-3 rounded-sm border border-border bg-background text-xs font-mono"
                  data-testid="playground-endpoint"
                >
                  {PLAYGROUND_ENDPOINTS.map(ep => (
                    <option key={ep.path + ep.method} value={ep.path + ep.method}>
                      {ep.method} {ep.path}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {selectedEp.method !== "GET" && (
              <div>
                <Label>Request body (JSON)</Label>
                <textarea value={playgroundBody} onChange={e => setPlaygroundBody(e.target.value)} rows={5}
                  className="w-full font-mono text-xs p-2 rounded-sm border border-border bg-background"
                  data-testid="playground-body" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button onClick={runPlayground} disabled={playgroundLoading} className="rounded-sm gap-2" data-testid="playground-run"><Play className="h-4 w-4" /> {playgroundLoading ? "Running…" : "Send"}</Button>
              <Button variant="outline" className="rounded-sm gap-2" onClick={() => copy(curlSample())} data-testid="playground-copy-curl"><Copy className="h-4 w-4" /> Copy cURL</Button>
              {playgroundResp && (
                <div className="text-xs ml-auto flex items-center gap-3">
                  <span className={`font-mono font-bold ${playgroundResp.status >= 200 && playgroundResp.status < 300 ? "text-emerald-700" : "text-red-700"}`}>{playgroundResp.status || "ERR"}</span>
                  <span className="text-muted-foreground">{playgroundResp.ms} ms</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Copy-paste cURL</div>
                <pre className="text-[10px] font-mono bg-muted/30 border border-border rounded-sm p-2 whitespace-pre-wrap break-all max-h-64 overflow-auto" data-testid="playground-curl">{curlSample()}</pre>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Response</div>
                <pre className="text-[10px] font-mono bg-muted/30 border border-border rounded-sm p-2 whitespace-pre-wrap max-h-64 overflow-auto" data-testid="playground-response">{playgroundResp?.body || "(run a request to see the response here)"}</pre>
              </div>
            </div>

            {historyForKey.length > 0 && (
              <div className="pt-2 border-t border-border" data-testid="playground-history">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recent runs ({historyForKey.length}/20)</div>
                  <Button variant="ghost" size="sm" className="rounded-sm text-[10px] h-6 text-red-600" onClick={clearHistory} data-testid="clear-history">Clear</Button>
                </div>
                <div className="space-y-1 max-h-56 overflow-auto">
                  {historyForKey.map(h => (
                    <button key={h.id} type="button" onClick={() => loadHistoryEntry(h)}
                      className="w-full text-left border border-border rounded-sm p-2 hover:bg-accent/40 flex items-center gap-2 text-xs" data-testid={`history-${h.id}`}>
                      <span className={`font-mono font-bold ${h.status >= 200 && h.status < 300 ? "text-emerald-700" : "text-red-700"}`}>{h.status || "ERR"}</span>
                      <span className="font-mono text-[10px] bg-muted/60 border border-border rounded-sm px-1.5">{h.method}</span>
                      <span className="font-mono flex-1 truncate">{h.path}</span>
                      <span className="text-muted-foreground text-[10px] font-mono">{h.ms} ms</span>
                      <span className="text-muted-foreground text-[10px] font-mono">{new Date(h.at).toLocaleTimeString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
